/**
 * SabaConfigForm — admin form for `/api/admin/saba/config`.
 *
 * Field rules
 * -----------
 * - `Signing Key` is a password input with placeholder "Leave blank to
 *   keep current". If the operator doesn't type anything we DO NOT send
 *   the field — matches the backend partial-update contract in
 *   `providers/saba/store.py::upsert_config_doc`.
 * - `Operator ID` is prefilled with the masked value (`***abcd`); the
 *   operator retypes the full id if they want to change it. We detect
 *   "still masked" by leading `***`.
 * - `Allowed Currencies` is a 4-checkbox multi-select.
 * - Save calls PUT, then refetches so we display the freshly-masked
 *   config (never any raw secret).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

import Button from "../ui-r1/Button";
import Input from "../ui-r1/Input";
import { Badge } from "../ui-r1/Badge";
import { Card, CardBody } from "../ui-r1/Card";
import {
  getAdminSabaConfig,
  updateAdminSabaConfig,
} from "../../lib/sabaApi";
import { extractError } from "../../lib/formatError";

const ALL_CURRENCIES = ["BDT", "INR", "PKR", "USDT"];

export default function SabaConfigForm() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(null);
  const [signingKey, setSigningKey] = useState(""); // separate — never lands in `form`
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  const reload = async () => {
    setLoadErr(null);
    try {
      const data = await getAdminSabaConfig();
      setCfg(data);
      setForm({
        enabled: !!data.enabled,
        operatorId: data.operatorId || "",
        baseUrl: data.baseUrl || "",
        requireKyc: !!data.requireKyc,
        allowedCurrencies: Array.isArray(data.allowedCurrencies) && data.allowedCurrencies.length
          ? data.allowedCurrencies
          : ALL_CURRENCIES,
      });
      setSigningKey("");
    } catch (e) {
      setLoadErr(extractError(e));
    }
  };

  useEffect(() => { reload(); }, []);

  const toggleCurrency = (code) => {
    setForm((f) => {
      const set = new Set(f.allowedCurrencies);
      if (set.has(code)) set.delete(code); else set.add(code);
      return { ...f, allowedCurrencies: [...set] };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    try {
      const payload = {
        enabled: form.enabled,
        baseUrl: form.baseUrl,
        requireKyc: form.requireKyc,
        allowedCurrencies: form.allowedCurrencies,
      };
      // Only send operatorId if the operator actually changed it away
      // from the masked-display value.
      if (form.operatorId && !form.operatorId.startsWith("***")) {
        payload.operatorId = form.operatorId;
      }
      // Only send signingKey when the operator typed one. Empty string
      // is treated as "keep current" — matches sabaApi.js normalization.
      if (signingKey) payload.signingKey = signingKey;

      await updateAdminSabaConfig(payload);
      toast.success("SABA config saved");
      await reload();
    } catch (err) {
      toast.error(extractError(err) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loadErr) {
    return (
      <Card>
        <CardBody className="py-10 text-center text-sm text-danger" data-testid="saba-config-load-error">
          Couldn&apos;t load SABA config: {loadErr}
        </CardBody>
      </Card>
    );
  }

  if (!form) {
    return (
      <Card>
        <CardBody className="py-10 flex items-center justify-center gap-2 text-ink-3 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading config…
        </CardBody>
      </Card>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid="saba-config-form">
      <Card>
        <CardBody className="space-y-5">
          {/* Header row — enable toggle + status pills. */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-1">Sportsbook</div>
              <div className="font-display italic text-2xl text-ink">Provider configuration</div>
              <div className="text-xs text-ink-3 mt-1">
                Live-toggle SABA. DB overrides <code className="text-ink-2">.env</code>; changes take
                effect immediately.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={cfg?.signingKeyConfigured ? "success" : "muted"} data-testid="saba-cfg-status-key">
                {cfg?.signingKeyConfigured ? "Key set" : "No key"}
              </Badge>
              <Badge variant={form.enabled ? "success" : "muted"} data-testid="saba-cfg-status-enabled">
                {form.enabled ? "Enabled" : "Disabled"}
              </Badge>
            </div>
          </div>

          {/* Toggles */}
          <div className="grid sm:grid-cols-2 gap-4">
            <ToggleField
              testid="saba-cfg-enabled"
              label="Enabled"
              hint="Turn SABA endpoints live. When off, wallet + session return 503."
              checked={form.enabled}
              onChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
            />
            <ToggleField
              testid="saba-cfg-require-kyc"
              label="Require KYC"
              hint="Block session launch unless the player is KYC-approved."
              checked={form.requireKyc}
              onChange={(v) => setForm((f) => ({ ...f, requireKyc: v }))}
            />
          </div>

          {/* Text fields */}
          <FieldLabel htmlFor="saba-cfg-operator-id" label="Operator ID">
            <Input
              id="saba-cfg-operator-id"
              type="text"
              value={form.operatorId}
              onChange={(e) => setForm((f) => ({ ...f, operatorId: e.target.value }))}
              placeholder="Provided by SABA onboarding"
              data-testid="saba-cfg-operator-id"
            />
          </FieldLabel>

          <FieldLabel htmlFor="saba-cfg-signing-key" label="Signing Key">
            <Input
              id="saba-cfg-signing-key"
              type="password"
              value={signingKey}
              onChange={(e) => setSigningKey(e.target.value)}
              placeholder="Leave blank to keep current"
              autoComplete="new-password"
              data-testid="saba-cfg-signing-key"
            />
            <div className="text-[10px] text-ink-3 mt-1">
              Never returned in any API response; stored in{" "}
              <code className="text-ink-2">provider_configs.saba</code>. Rotations audit to{" "}
              <code className="text-ink-2">audit_events</code>.
            </div>
          </FieldLabel>

          <FieldLabel htmlFor="saba-cfg-base-url" label="Base URL">
            <Input
              id="saba-cfg-base-url"
              type="url"
              value={form.baseUrl}
              onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
              placeholder="https://sandbox.saba-sports.example"
              data-testid="saba-cfg-base-url"
            />
          </FieldLabel>

          {/* Multi-select: allowed currencies */}
          <FieldLabel label="Allowed currencies">
            <div className="flex flex-wrap gap-2" data-testid="saba-cfg-currencies">
              {ALL_CURRENCIES.map((c) => {
                const on = form.allowedCurrencies.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCurrency(c)}
                    className={
                      "h-8 px-3 rounded-md border text-[11px] tracking-widest uppercase transition " +
                      (on
                        ? "bg-gold/10 border-gold/60 text-gold shadow-glow-gold"
                        : "bg-highest border-line text-ink-3 hover:text-ink hover:border-ink-3")
                    }
                    data-testid={`saba-cfg-cur-${c}`}
                    aria-pressed={on}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </FieldLabel>

          {/* Meta */}
          {(cfg?.updatedAt || cfg?.updatedBy) && (
            <div className="text-[10px] tracking-widest uppercase text-ink-3 border-t border-line pt-3">
              Last change {cfg.updatedAt || "—"} · by {cfg.updatedBy || "—"}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              variant="primary"
              disabled={saving}
              data-testid="saba-cfg-save"
            >
              {saving
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                : <><Save className="w-4 h-4 mr-2" />Save changes</>}
            </Button>
          </div>
        </CardBody>
      </Card>
    </form>
  );
}


function FieldLabel({ htmlFor, label, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <div className="text-[10px] tracking-widest uppercase text-ink-3 mb-1.5">{label}</div>
      {children}
    </label>
  );
}


function ToggleField({ testid, label, hint, checked, onChange }) {
  return (
    <div className="rounded-lg border border-line bg-highest px-4 py-3 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm text-ink font-medium">{label}</div>
        <div className="text-[11px] text-ink-3">{hint}</div>
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        role="switch"
        aria-checked={checked}
        className={
          "relative w-11 h-6 rounded-full transition-colors " +
          (checked ? "bg-gold" : "bg-line")
        }
        data-testid={testid}
      >
        <span
          className={
            "absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform " +
            (checked ? "translate-x-5" : "translate-x-0")
          }
        />
      </button>
    </div>
  );
}
